import { Link, Outlet, useMatches } from "@tanstack/react-router";
import { ArrowLeft, KeyRound, Menu, OctagonX, Search, ShieldHalf } from "lucide-react";
import { useEffect, useState } from "react";
import { Badge, Button, Callout, ErrorState, IconButton, Kbd, Sheet, SheetContent, StatusDot, toast, type Tone } from "@/design";
import { appUrl, onAdminHost } from "@/lib/adminHost";
import { useAddPasskey, useAdminKillSwitch, useAdminOverview, useMe, useMyPasskeys, type AdminOverview } from "@/lib/api";
import { PasskeyCancelled } from "@/lib/passkey";
import { deviceLabel } from "@/routes/-signup/SignupFlow";
import { cn } from "@/lib/cn";
import { adminNav, type AdminNavItem } from "./adminNav";
import { CommandPalette } from "./CommandPalette";
import { openCommandPalette } from "./commands";
import { Logo } from "./Logo";
import { Shortcuts } from "./Shortcuts";
import { ThemeMenu, UserMenu } from "./TopBar";

const navItem = cn(
  "group flex h-8 items-center gap-2.5 rounded-md px-2.5 text-base font-medium text-muted transition-colors",
  "hover:bg-surface-3/60 hover:text-text [&_svg]:size-4 [&_svg]:shrink-0 [&>svg]:text-subtle hover:[&>svg]:text-muted",
  "data-[status=active]:bg-surface data-[status=active]:text-text data-[status=active]:shadow-xs data-[status=active]:ring-1 data-[status=active]:ring-border data-[status=active]:[&>svg]:text-accent-text",
);

const platformTone: Record<AdminOverview["platform"]["status"], Tone> = { operational: "ok", degraded: "warn", outage: "danger" };
const platformLabel: Record<AdminOverview["platform"]["status"], string> = { operational: "Operational", degraded: "Degraded", outage: "Outage" };

/** Platform admin layout (founder only): its own sidebar and top bar, the shared palette, theme and account menus. */
export function AdminShell() {
  const [mobileNav, setMobileNav] = useState(false);
  useAdminDocumentTitle();
  return (
    <div className="flex min-h-dvh">
      <a href="#main" className="sr-only z-50 rounded-md bg-surface px-3 py-2 focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:shadow-lg">
        Skip to content
      </a>
      <aside className="sticky top-0 hidden h-dvh w-[252px] shrink-0 border-r border-border bg-sidebar lg:block">
        <AdminSidebar />
      </aside>
      <Sheet open={mobileNav} onOpenChange={setMobileNav}>
        <SheetContent side="left" size="sm" title="Menu" className="bg-sidebar [&>div:first-child]:hidden [&>div:nth-child(2)]:p-0">
          <AdminSidebar onNavigate={() => setMobileNav(false)} />
        </SheetContent>
      </Sheet>
      <div className="flex min-w-0 flex-1 flex-col">
        <KillSwitchBanner />
        <AdminTopBar onMenu={() => setMobileNav(true)} />
        <AdminPasskeyCallout />
        <main id="main" className="flex-1" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
      <CommandPalette admin />
      <Shortcuts admin />
    </div>
  );
}

function useAdminDocumentTitle() {
  const matches = useMatches();
  const title = [...matches].reverse().find((m) => m.staticData?.title)?.staticData.title;
  useEffect(() => {
    document.title = [title, "Admin", "Guv"].filter(Boolean).join(" · ");
  }, [title]);
}

function navCount(key: AdminNavItem["key"], o: AdminOverview | undefined): { n: number; tone: Tone } | null {
  if (!o) return null;
  switch (key) {
    case "overview": {
      const critical = o.attention.filter((a) => a.severity === "critical").length;
      return critical ? { n: critical, tone: "danger" } : o.attention.length ? { n: o.attention.length, tone: "warn" } : null;
    }
    case "servers": {
      const down = o.capacity.nodes_total - o.capacity.nodes_ready;
      return down ? { n: down, tone: "warn" } : null;
    }
    case "incidents":
      return o.incidents_open ? { n: o.incidents_open, tone: "danger" } : null;
    case "ai-ops":
      return o.ai_ops.awaiting_approval ? { n: o.ai_ops.awaiting_approval, tone: "warn" } : null;
    case "operations":
      return o.counts.builds_running + o.counts.builds_queued ? { n: o.counts.builds_running + o.counts.builds_queued, tone: "info" } : null;
    case "signups":
      return o.signups.waitlist_confirmed ? { n: o.signups.waitlist_confirmed, tone: "neutral" } : null;
    case "money":
      return o.money.unpaid_invoices ? { n: o.money.unpaid_invoices, tone: "warn" } : null;
    default:
      return null;
  }
}

function AdminSidebar({ onNavigate }: { onNavigate?: () => void }) {
  const overview = useAdminOverview();
  const me = useMe();
  const home = me.data?.workspaces[0]?.slug;
  const o = overview.data;
  const groups = ["Platform", "Customers", "Business"] as const;
  const item = (n: AdminNavItem) => {
    const c = navCount(n.key, o);
    return (
      <Link key={n.key} to={n.to} activeOptions={{ exact: !!n.exact }} className={navItem} onClick={onNavigate}>
        {n.icon}
        <span className="flex-1 truncate">{n.label}</span>
        {c && (
          <Badge tone={c.tone} size="sm" className="tabular">
            {c.n}
          </Badge>
        )}
      </Link>
    );
  };
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-14 items-center gap-2 px-4">
        <Link to="/admin" onClick={onNavigate} className="rounded-md">
          <Logo />
        </Link>
        <Badge tone="danger" variant="outline" size="sm" icon={<ShieldHalf />}>
          Admin
        </Badge>
      </div>
      <div className="px-3">
        <div className="flex items-center gap-2.5 rounded-lg border border-border bg-surface px-3 py-2.5 shadow-xs">
          <StatusDot tone={o ? platformTone[o.platform.status] : "neutral"} pulse={o?.platform.status !== "operational"} />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-text">{o ? platformLabel[o.platform.status] : "Checking…"}</div>
            <div className="truncate text-xs text-subtle">{o ? `${o.capacity.nodes_ready} of ${o.capacity.nodes_total} servers ready` : " "}</div>
          </div>
        </div>
      </div>
      <nav className="scrollbar-thin mt-4 flex-1 space-y-5 overflow-y-auto px-3 pb-4" aria-label="Admin">
        <div className="space-y-0.5">{adminNav.filter((n) => n.group === null).map(item)}</div>
        {groups.map((g) => (
          <div key={g}>
            <div className="mb-1.5 px-2.5 text-2xs font-semibold uppercase tracking-[0.08em] text-subtle">{g}</div>
            <div className="space-y-0.5">{adminNav.filter((n) => n.group === g).map(item)}</div>
          </div>
        ))}
      </nav>
      {home && (
        <div className="border-t border-border p-3">
          {onAdminHost ? (
            <a href={appUrl(`/${home}`)} className={navItem}>
              <ArrowLeft /> Back to your projects
            </a>
          ) : (
            <Link to="/$workspace" params={{ workspace: home }} onClick={onNavigate} className={navItem}>
              <ArrowLeft /> Back to your projects
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function AdminTopBar({ onMenu }: { onMenu: () => void }) {
  const matches = useMatches();
  const crumbs = matches.filter((m) => m.staticData?.title).map((m) => ({ id: m.id, title: m.staticData.title!, path: m.pathname }));
  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border bg-canvas/85 px-3 backdrop-blur-md supports-[backdrop-filter]:bg-canvas/70 sm:px-4 lg:px-6">
      <IconButton label="Open menu" className="lg:hidden" onClick={onMenu}>
        <Menu />
      </IconButton>
      <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5 text-base">
        <Link to="/admin" className="hidden text-muted hover:text-text sm:block">
          Admin
        </Link>
        {crumbs
          .filter((c) => c.title !== "Overview")
          .map((c, i, all) => (
            <span key={c.id} className="flex min-w-0 items-center gap-1.5">
              <span className="hidden text-subtle sm:inline" aria-hidden="true">
                /
              </span>
              {i === all.length - 1 ? (
                <span className="truncate font-semibold text-text" aria-current="page">
                  {c.title}
                </span>
              ) : (
                <Link to={c.path} className="truncate text-muted hover:text-text">
                  {c.title}
                </Link>
              )}
            </span>
          ))}
      </nav>
      <div className="ml-auto flex items-center gap-1">
        <button
          type="button"
          onClick={openCommandPalette}
          className="mr-1 hidden h-8 w-64 items-center gap-2 rounded-md border border-border bg-surface px-2.5 text-sm text-subtle shadow-xs transition-colors hover:border-border-strong hover:text-muted md:flex"
        >
          <Search className="size-4" />
          <span className="flex-1 text-left">Jump to…</span>
          <Kbd mod>K</Kbd>
        </button>
        <IconButton label="Search" className="md:hidden" onClick={openCommandPalette}>
          <Search />
        </IconButton>
        <ThemeMenu />
        <UserMenu />
      </div>
    </header>
  );
}

/** Impossible to miss while agents are stopped. */
function KillSwitchBanner() {
  const ks = useAdminKillSwitch();
  if (!ks.data?.engaged) return null;
  return (
    <div role="alert" className="flex items-center gap-3 bg-danger px-4 py-2 text-sm font-medium text-on-solid sm:px-6">
      <OctagonX className="size-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate">
        AI operations are stopped{ks.data.reason ? `: ${ks.data.reason}` : "."} Agents can read but cannot change anything.
      </span>
      <Link to="/admin/ai-ops" className="shrink-0 underline underline-offset-2">
        Review
      </Link>
    </div>
  );
}


/**
 * Passkeys belong to the host they were made on, so the ones from app.guv.dev do not work here. Drain, suspend,
 * disable and the kill switch all need a passkey step-up, so ask for an admin one until there is one.
 */
function AdminPasskeyCallout() {
  const keys = useMyPasskeys(onAdminHost);
  const add = useAddPasskey();
  const [error, setError] = useState<unknown>(null);
  if (!onAdminHost || !keys.data || keys.data.data.some((k) => k.rp_id === window.location.hostname)) return null;
  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 pt-6 sm:px-6 lg:px-10">
      <Callout
        tone="warn"
        icon={<KeyRound />}
        title="Add a passkey for the admin console"
        actions={
          <Button
            size="sm"
            variant="primary"
            loading={add.isPending}
            onClick={() => {
              setError(null);
              add.mutate(`${deviceLabel()} (admin)`, {
                onSuccess: () => toast({ title: "Admin passkey added", tone: "success" }),
                onError: (e) => !(e instanceof PasskeyCancelled) && setError(e),
              });
            }}
          >
            Add passkey
          </Button>
        }
      >
        Your passkeys from the dashboard only work there. Draining a server, suspending a tenant, disabling an account and restarting AI operations need one made here.
        {error != null && <ErrorState className="mt-2" error={error} variant="inline" />}
      </Callout>
    </div>
  );
}
